// 포트빌 관리비 청구 시스템 타입 정의

/**
 * 관리비 청구서 인터페이스
 * Firestore 컬렉션: portville_bills
 */
export interface PortvilleBill {
  id: string;
  roomId: string;
  roomNumber: string;
  roomType: '호실' | '시설물'; // 청구서 타입 구분
  residentUid?: string; // UID가 있는 경우 (호실인 경우)
  residentInfo?: {  // 호실인 경우
    name: string;
    phoneNumber: string;
    organization: string;
  };
  facilityInfo?: {  // 시설물인 경우
    facilityType: string;
    facilityName: string;
    responsiblePerson: {
      name: string;
      phoneNumber: string;
      organization: string;
    };
  };
  
  // 청구 기간
  billingPeriod: {
    year: number;
    month: number;
    startDate: Date;
    endDate: Date;
  };
  
  // 관리비 항목별 금액
  billItems: {
    monthlyRent: number;          // 월 임대료
    managementFee: number;        // 관리비
    managementFeeVAT: number;     // 부가세 (관리비)
    electricityFee: number;       // 전기요금 (기본료 + 개별료 + 공용료)
    electricityFeeVAT: number;    // 전기요금 (부가세)
    electricityFund: number;      // 전력기금
    waterFee: number;             // 수도요금 (개별료 + 공용료)
    communicationFee: number;     // 통신요금
    communicationFeeVAT: number;  // 통신요금 (부가세)
    offsetProcessingFee: number;  // 상계처리비
    offsetProcessingFeeVAT: number; // 상계처리비 (부가세)
    overdueAmount: number;        // 미납금
    overduePenalty: number;       // 가산금
    expectedPenalty: number;      // 예상 가산금 (다음달에 청구할 금액)
  };
  
  // 사용량 정보 (고지서 표기용)
  usageInfo?: {
    electricityIndividualUsage?: number; // 개별 전기 사용량
    electricityCommonUsage?: number;     // 공용 전기 사용량
    waterIndividualUsage?: number;       // 개별 수도 사용량
    waterCommonUsage?: number;           // 공용 수도 사용량
  };
  
  // 총액
  totalAmount: number;
  
  // 청구 상태
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  
  // 일자 정보
  issuedDate: Date;     // 청구서 발행일
  dueDate: Date;        // 납부 기한
  paidDate?: Date;      // 납부일 (납부 완료 시)
  
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 관리비 설정 인터페이스
 * Firestore 컬렉션: portville_billing_settings
 */
export interface PortvilleBillingSettings {
  id: string;
  roomId: string;
  roomNumber: string;
  roomType: '호실' | '시설물'; // 호실 또는 시설물 구분

  // 연락처 정보
  contactInfo?: {
    contractorPhoneNumber?: string; // 계약자 연락처
    roommatePhoneNumber?: string;   // 동거인 연락처
  };

  // 기본 요금 설정
  baseRates: {
    monthlyRent: number;          // 월 임대료
    managementFee: number;        // 관리비
    managementFeeVATRate: number; // 관리비 부가세율 (기본 10%)

    // 기타 요금
    communicationFee: number;      // 통신요금
    communicationFeeVATRate: number; // 통신요금 부가세율
  };

  // 호실별 설정
  roomSettings: {
    electricityAreaContributionRatio: number; // 전기 면적기여비율 (기본값: 0.0040329)
    waterAreaContributionRatio: number; // 수도 면적기여비율 (기본값: 0.0040329)
    roomType: '포트빌' | '늘해랑' | '전기차 충전소' | '통신 중계기' | '기타';  // 호실타입 또는 시설물타입
  };

  // 청구 설정
  billingConfig: {
    issueDay: number;     // 매월 청구서 발행일 (1-28)
    dueDay: number;       // 매월 납부 기한일 (1-31)
    autoGenerate: boolean; // 자동 청구서 생성 여부
  };

  // 적용 기간
  effectiveFrom: Date;
  effectiveTo?: Date;

  // 버전 관리 필드 추가
  version: number;                    // 설정 버전 (1부터 시작)
  parentSettingsId?: string;          // 이전 버전의 설정 ID
  isActive: boolean;                  // 현재 활성 상태 (true면 현재 사용 중)

  // 변경 추적 필드
  changeReason?: string;              // 변경 사유
  approvedBy?: string;                // 승인자 UID (관리자 이상)

  // 자동화 지원 필드
  autoApplyOnResidencyChange: boolean; // 입주자 변동 시 자동 적용 여부
  notificationSent: boolean;          // 변경 알림 발송 여부

  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // 설정한 관리자 UID
}

/**
 * 건물 전체 사용량 기록 인터페이스
 * Firestore 컬렉션: portville_building_usage_records
 */
export interface PortvilleBuildingUsageRecord {
  id: string;
  
  // 사용량 정보
  period: {
    year: number;
    month: number;
  };
  
  // 전기 관련
  totalElectricityFee: number;        // 전체전력량요금
  buildingIndividualUsage: number;    // 건물 개별사용량 (kWh)
  buildingCommonUsage: number;        // 건물 공동사용량 (kWh)
  totalBaseFee: number;               // 전체기본요금
  totalOffsetFee: number;             // 전체상계거래요금
  totalElectricityFund: number;       // 전체전력기금
  
  // 수도 관련
  totalWaterFee: number;              // 전체수도요금
  buildingWaterIndividualUsage: number; // 건물개별사용량 (톤)
  buildingWaterCommonUsage: number;     // 건물공동사용량 (톤)

  // 포트빌 수도 관련
  portvilleTotalWaterFee: number;
  portvilleBuildingWaterIndividualUsage: number;
  portvilleBuildingWaterCommonUsage: number;

  // 늘해랑 수도 관련
  neulhaerangTotalWaterFee: number;
  neulhaerangBuildingWaterIndividualUsage: number;
  neulhaerangBuildingWaterCommonUsage: number;

  // 기록 정보
  recordedAt: Date;
  recordedBy: string; // 기록한 관리자 UID
  
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 호실별 사용량 기록 인터페이스
 * Firestore 컬렉션: portville_usage_records
 */
export interface PortvilleUsageRecord {
  id: string;
  roomId: string;
  roomNumber: string;
  
  // 사용량 정보
  period: {
    year: number;
    month: number;
  };
  
  // 전기 사용량 (kWh) - 음수 허용
  electricityIndividualUsage?: number; // 개별 전기 사용량
  electricityCommonUsage?: number;     // 공용 전기 사용량
  
  // 수도 사용량 (톤) - 음수 허용
  waterIndividualUsage?: number;       // 개별 수도 사용량
  waterCommonUsage?: number;           // 공용 수도 사용량
  
  // 기록 정보
  recordedAt: Date;
  recordedBy: string; // 기록한 관리자 UID
  
  createdAt: Date;
  updatedAt: Date;
}

/**
 * SMS 인증 임시 토큰 인터페이스
 * Firestore 컬렉션: portville_billing_auth_tokens
 */
export interface PortvilleBillingAuthToken {
  id: string;
  roomNumber: string;
  phoneNumber: string;
  verificationCode: string;
  
  expiresAt: Date;
  isUsed: boolean;
  
  createdAt: Date;
}

/**
 * 관리비 설정 폼 데이터 타입
 */
/**
 * 관리비 설정 변경 히스토리 인터페이스
 * Firestore 컬렉션: portville_billing_settings_history
 */
export interface PortvilleBillingSettingsHistory {
  id: string;
  settingsId: string;  // 원본 설정 문서 ID
  version: number;     // 이 히스토리 레코드의 버전 번호

  // 변경 전 설정 데이터 (전체 스냅샷)
  previousSettings: Omit<PortvilleBillingSettings, 'id'>;

  // 변경 후 설정 데이터 (전체 스냅샷)
  currentSettings: Omit<PortvilleBillingSettings, 'id'>;

  // 변경 메타데이터
  changeType: 'create' | 'update' | 'deactivate';
  changeReason: string;
  changedFields: string[];  // 변경된 필드명 목록

  // 승인 정보
  requestedBy: string;      // 변경 요청자 UID
  approvedBy?: string;      // 승인자 UID
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvalComment?: string; // 승인/거절 사유

  // 시간 정보
  requestedAt: Date;
  processedAt?: Date;       // 승인/거절 처리 시간

  createdAt: Date;
  updatedAt: Date;
}

/**
 * 입주자 변동 감지 및 알림 인터페이스
 * Firestore 컬렉션: portville_residency_changes
 */
export interface PortvilleResidencyChange {
  id: string;
  roomId: string;
  roomNumber: string;

  // 변동 정보
  changeType: 'move_in' | 'move_out' | 'contract_renewal' | 'contact_change';

  // 이전/신규 입주자 정보
  previousResident?: {
    name: string;
    contractorPhone: string;
    roommatePhone?: string;
    contractEndDate?: Date;
  };

  newResident?: {
    name: string;
    contractorPhone: string;
    roommatePhone?: string;
    contractStartDate: Date;
    contractEndDate?: Date;
  };

  // 관리비 설정 영향
  requiresSettingsUpdate: boolean;
  currentSettingsId?: string;      // 현재 적용 중인 설정 ID
  suggestedSettingsUpdate?: {      // 추천 설정 업데이트 내용
    effectiveFrom: Date;
    changes: Partial<PortvilleBillingSettings>;
  };

  // 처리 상태
  detectionMethod: 'manual' | 'auto_sms' | 'auto_payment' | 'admin_input';
  status: 'detected' | 'confirmed' | 'settings_updated' | 'completed';
  processedBy?: string;            // 처리한 관리자 UID

  // 알림 상태
  notificationSent: boolean;
  notificationSentAt?: Date;

  createdAt: Date;
  updatedAt: Date;
}

/**
 * 시점별 설정 조회 결과 인터페이스
 */
export interface PortvilleBillingSettingsSnapshot {
  settingsId: string;
  roomId: string;
  roomNumber: string;

  // 조회 시점 정보
  effectiveDate: Date;           // 요청된 기준 날짜
  snapshotDate: Date;            // 실제 설정이 유효했던 날짜

  // 해당 시점의 설정 정보
  settings: PortvilleBillingSettings;

  // 컨텍스트 정보
  isCurrentSettings: boolean;    // 현재 활성 설정인지 여부
  nextChangeDate?: Date;         // 다음 설정 변경 예정일
  historyCount: number;          // 총 변경 이력 개수
}

export interface BillingSettingsFormData {
  roomId: string;
  roomNumber: string;
  roomType: '호실' | '시설물';

  // 연락처 정보
  contractorPhoneNumber?: string; // 계약자 연락처 (필수)
  roommatePhoneNumber?: string;   // 동거인 연락처 (선택사항)

  // 기본 요금
  monthlyRent?: number;
  managementFee?: number;
  managementFeeVATRate?: number;

  // 기타 요금
  communicationFee?: number;
  communicationFeeVATRate?: number;

  // 호실별 설정
  electricityAreaContributionRatio?: number; // 전기 면적기여비율
  waterAreaContributionRatio?: number; // 수도 면적기여비율
  roomTypeDetail?: '포트빌' | '늘해랑' | '전기차 충전소' | '통신 중계기' | '기타'; // 호실/시설물의 세부 타입

  // 청구 설정
  issueDay: number;
  dueDay: number;
  autoGenerate: boolean;

  // 적용 기간
  effectiveFrom: string; // ISO date string
  effectiveTo?: string;  // ISO date string

  // 버전 관리 (폼에서 사용)
  changeReason?: string;
  autoApplyOnResidencyChange?: boolean;
}

/**
 * 청구서 생성 요청 데이터 타입
 */
export interface BillGenerationRequest {
  roomIds: string[]; // 청구서를 생성할 호실 ID 목록
  billingPeriod: {
    year: number;
    month: number;
  };
  issuedDate: Date;
  // dueDate는 자동으로 청구월의 다음달 말일로 계산됨
}

/**
 * 건물 전체 사용량 입력 폼 데이터 타입
 */
export interface BuildingUsageRecordFormData {
  year: number;
  month: number;

  // 전기 관련
  totalElectricityFee: number;
  buildingIndividualUsage: number;
  buildingCommonUsage: number;
  totalBaseFee: number;
  totalOffsetFee: number;
  totalElectricityFund: number;

  // 포트빌 수도 관련
  portvilleTotalWaterFee: number;
  portvilleBuildingWaterIndividualUsage: number;
  portvilleBuildingWaterCommonUsage: number;

  // 늘해랑 수도 관련
  neulhaerangTotalWaterFee: number;
  neulhaerangBuildingWaterIndividualUsage: number;
  neulhaerangBuildingWaterCommonUsage: number;

  // 이전 호환성을 위한 필드 (deprecated)
  totalWaterFee?: number;
  buildingWaterIndividualUsage?: number;
  buildingWaterCommonUsage?: number;
}

/**
 * 호실별 요금 계산 결과 타입
 */
export interface RoomBillingCalculation {
  roomId: string;
  roomNumber: string;
  residentName: string;
  
  // 전기 요금 상세
  electricity: {
    baseFee: number;           // 기본요금
    commonFee: number;         // 공동전기료
    individualFee: number;     // 전력량요금
    totalFee: number;          // 전기요금 합계
    fund: number;              // 전력기금
    offsetFee: number;         // 상계처리비
  };
  
  // 수도 요금 상세
  water: {
    individualFee: number;     // 개별수도료
    commonFee: number;         // 공동수도료
    totalFee: number;          // 수도요금 합계
  };
  
  // 사용량
  usage: {
    electricityIndividual: number;  // 개별 전기 사용량
    electricityCommon: number;      // 공용 전기 사용량
    waterIndividual: number;        // 개별 수도 사용량
    waterCommon: number;            // 공용 수도 사용량
  };
  
  // 계산 비율
  ratios: {
    electricityAreaContribution: number; // 전기 면적기여비율
    waterAreaContribution: number;       // 수도 면적기여비율
    electricityIndividual: number;       // 전기 개별 사용량 비율
    waterIndividual: number;             // 수도 개별 사용량 비율
  };
}

/**
 * 호실별 요금 계산 결과 저장용 인터페이스
 * Firestore 컬렉션: portville_billing_calculations
 */
export interface PortvilleBillingCalculation {
  id: string;
  year: number;
  month: number;
  roomId: string;
  roomNumber: string;
  residentName: string;
  
  // 전기 요금 상세
  electricity: {
    baseFee: number;           // 기본요금
    commonFee: number;         // 공동전기료
    individualFee: number;     // 전력량요금
    totalFee: number;          // 전기요금 합계
    fund: number;              // 전력기금
    offsetFee: number;         // 상계처리비
  };
  
  // 수도 요금 상세
  water: {
    individualFee: number;     // 개별수도료
    commonFee: number;         // 공동수도료
    totalFee: number;          // 수도요금 합계
  };
  
  // 사용량
  usage: {
    electricityIndividual: number;  // 개별 전기 사용량
    electricityCommon: number;      // 공용 전기 사용량
    waterIndividual: number;        // 개별 수도 사용량
    waterCommon: number;            // 공용 수도 사용량
  };
  
  // 계산 비율
  ratios: {
    areaContribution: number;       // 면적기여비율
    electricityIndividual: number;  // 전기 개별 사용량 비율
    waterIndividual: number;        // 수도 개별 사용량 비율
  };
  
  // 메타데이터
  createdAt: Date;
  updatedAt: Date;
  buildingUsageRecordId: string;  // 건물 전체 사용량 기록 ID
}

/**
 * 호실별 사용량 입력 폼 데이터 타입
 */
export interface UsageRecordFormData {
  roomId: string;
  roomNumber: string;
  year: number;
  month: number;
  electricityIndividualUsage?: number;
  electricityCommonUsage?: number;
  waterIndividualUsage?: number;
  waterCommonUsage?: number;
}

/**
 * SMS 인증 요청 데이터 타입
 */
export interface SmsAuthRequest {
  roomNumber: string;
  phoneNumber: string;
}

/**
 * SMS 인증 확인 데이터 타입
 */
export interface SmsAuthVerification {
  roomNumber: string;
  phoneNumber: string;
  verificationCode: string;
}

/**
 * 관리비 조회 결과 타입 (입주자용)
 */
export interface BillingViewData {
  roomInfo: {
    roomNumber: string;
    residentInfo: {
      name: string;
      phoneNumber: string;
      organization: string;
    };
  };
  currentBill?: PortvilleBill; // 당월 청구서
  previousBills: PortvilleBill[]; // 이전 청구서들
  totalUnpaidAmount: number; // 미납 총액
}

/**
 * 청구서 상태별 라벨
 */
export const BILL_STATUS_LABELS = {
  pending: '납부대기',
  paid: '납부완료',
  overdue: '연체',
  cancelled: '취소'
} as const;

/**
 * 청구서 상태별 색상
 */
export const BILL_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
} as const;

/**
 * 관리비 항목별 라벨
 */
export const BILL_ITEM_LABELS = {
  monthlyRent: '월 임대료',
  managementFee: '관리비',
  managementFeeVAT: '관리비 부가세',
  electricityFee: '전기요금',
  electricityFeeVAT: '전기요금 부가세',
  electricityFund: '전력기금',
  waterFee: '수도요금',
  communicationFee: '통신요금',
  communicationFeeVAT: '통신요금 부가세',
  offsetProcessingFee: '상계처리비',
  offsetProcessingFeeVAT: '상계처리비 부가세'
} as const;

/**
 * 계산 방식별 라벨
 */

/**
 * 관리비 계산 유틸리티 타입
 */
export interface BillCalculationInput {
  settings: PortvilleBillingSettings;
  usageRecord?: PortvilleUsageRecord;
  buildingUsageRecord?: PortvilleBuildingUsageRecord;
  roomBillingCalculation?: RoomBillingCalculation;
  billingPeriod: {
    year: number;
    month: number;
  };
}

/**
 * 관리비 통계 데이터 타입
 */
export interface BillingStatistics {
  totalRooms: number;
  billedRooms: number;
  paidRooms: number;
  overdueRooms: number;
  totalBilledAmount: number;
  totalPaidAmount: number;
  totalOverdueAmount: number;
  collectionRate: number; // 수납률 (%)
}

/**
 * 월별 관리비 통계 타입
 */
export interface MonthlyBillingStats {
  period: {
    year: number;
    month: number;
  };
  stats: BillingStatistics;
}

/**
 * 에러 타입들
 */
export class BillingError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'BillingError';
  }
}

export class BillGenerationError extends BillingError {
  constructor(message: string, public roomId?: string, details?: any) {
    super(message, 'BILL_GENERATION_ERROR', details);
    this.name = 'BillGenerationError';
  }
}

export class SmsAuthError extends BillingError {
  constructor(message: string, details?: any) {
    super(message, 'SMS_AUTH_ERROR', details);
    this.name = 'SmsAuthError';
  }
}

